<template>
  <div
    class="money-root"
    :class="{ 'money-root--mobile-numpad': useMobileNumpad }"
    @click="useMobileNumpad ? onMobileFieldClick($event) : null"
  >
    <v-text-field
      ref="refinput"
      v-model="cmpValue"
      v-disabled-icon-focus
      :label="label"
      :flat="flat"
      :solo="solo"
      type="tel"
      :placeholder="placeholder"
      :readonly="readonly || useMobileNumpad"
      :disabled="disabled"
      :outlined="outlined"
      :dense="dense"
      :hide-details="hideDetails"
      :error="error"
      :error-messages="errorMessages"
      :rules="rules"
      :height="height"
      :clearable="clearable"
      :class="{ 'input-new-style': newStyle }"
      :background-color="
        newStyle ? '#e5e5e5' : $vuetify.theme.isDark ? '#272727' : 'white'
      "
      :prefix="options.prefix"
      :suffix="options.suffix"
      v-bind="properties"
      :autofocus="autofocus"
      @keypress="keyPress"
      @blur="onBlur($event)"
      @focus="onFocus"
    >
      <template v-slot:append>
        <v-icon
          v-if="appendIcon"
          color="primary"
          style="font-size: 18px; margin-top: 2px"
          @click="$emit('append')"
        >
          {{ appendIcon }}
        </v-icon>
        <v-btn
          v-show="cmpValue && mClearable"
          color="primary"
          icon
          small
          style="margin-top: 9px"
          tabindex="-1"
          @click.stop.prevent="cmpValue = null"
        >
          <v-icon style="font-size: 26px"> mdi-close </v-icon>
        </v-btn>
      </template>
      <template v-slot:prepend-inner>
        <v-btn
          v-if="numpad && !useMobileNumpad"
          color="primary"
          icon
          x-small
          tabindex="-1"
          @click.stop="openNumpad"
        >
          <v-icon style="font-size: 26px; margin-top: 2px"> mdi-calculator </v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <!-- Desktop / explicit numpad prop -->
    <v-dialog v-if="!useMobileNumpad" v-model="numpadDialog" width="325">
      <v-card style="background-color: #e3e3e3">
        <v-col class="d-flex pt-2 pb-0">
          <v-btn icon @click="numpadDialog = false">
            <v-icon class="close-hover" large color="grey"> mdi-close </v-icon>
          </v-btn>
          <h3 class="mx-auto font-weight-medium mt-2">
            {{ $t(numpadLable) }}
          </h3>
        </v-col>
        <v-card-text class="pa-0">
          <div class="overflow-y-auto ma-1 mx-0 mt-0">
            <numpad :input="numpadValue" :label="numpadLable" @save="setNumpad" />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  directives: {
    disabledIconFocus: {
      bind(el) {
        el.querySelectorAll(".v-input__icon button").forEach((x) =>
          x.setAttribute("tabindex", -1)
        );
      },
    },
  },
  components: {
    numpad: () => import("./../numpad/index.vue"),
  },
  model: { prop: "value", event: "input" },
  props: {
    newStyle: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    numpad: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: "0",
    },
    label: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "",
    },
    mClearable: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    appendIcon: {
      type: String,
      default: undefined,
    },
    flat: {
      type: Boolean,
      default: true,
    },
    percent: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
    errorMessages: {
      type: [Array, String],
      default: () => [],
    },
    rules: {
      type: [Array, String],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: "white",
    },
    valueWhenIsEmpty: {
      type: String,
      default: "",
    },
    valueOptions: {
      type: Object,
      default: function () {
        return {
          min: 0,
          minEvent: "SetValueMin",
          max: 99999999999999999999999,
          maxEvent: "SetValueMax",
        };
      },
    },
    options: {
      type: Object,
      default: function () {
        return {
          locale: "pt-BR",
          prefix: "",
          suffix: "",
          length: 11,
          precision: 2,
        };
      },
    },
    properties: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data: () => ({
    numpadDialog: false,
    setDecimal: false,
    numpadValue: 0,
    numpadLable: null,
    decimalNumber: 0,
    percentStatus: false,
    mobileNumpadDraft: "0",
    mobileNumpadPrevious: 0,
    mobileNumpadReplaceNext: true,
    _mobileOverlayEl: null,
  }),
  computed: {
    useMobileNumpad() {
      return !!this.isMobile && !this.disabled;
    },
    mobileNumpadDisplay() {
      const raw = String(this.mobileNumpadDraft == null ? "0" : this.mobileNumpadDraft);
      if (raw.endsWith(".")) {
        const base = raw.slice(0, -1) || "0";
        return `${this.formatDraft(base)}.`;
      }
      return this.formatDraft(raw);
    },
    cmpValue: {
      get: function () {
        if (this.value !== null && this.value !== "" && this.value > 0) {
          var num = this.value.toString().replace(/,/g, "");
          if (this.options.precision !== undefined && this.options.precision > 0) {
            num = parseFloat(num).toFixed(4);
          } else {
            num = parseFloat(num).toFixed(10);
          }
          var ex = parseFloat(num)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          return ex;
        } else {
          return this.valueWhenIsEmpty;
        }
      },
      set: function (newValue) {
        var clearNumber;
        if (newValue) {
          clearNumber = newValue.replace(/,/g, "");
          if (this.options.precision !== undefined && this.options.precision > 0) {
            clearNumber = parseFloat(clearNumber).toFixed(4);
          } else {
            clearNumber = parseFloat(clearNumber).toFixed(10);
          }
        } else {
          clearNumber = newValue;
        }
        this.$emit("input", clearNumber);
        return clearNumber;
      },
    },
  },
  watch: {
    percentStatus() {
      this.cmpValue = 0;
      this.$emit("input", 0);
      this.$emit("changePercent", this.percentStatus);
    },
    isMobile(val) {
      if (!val) {
        this.destroyMobileOverlay();
        this.numpadDialog = false;
      }
    },
    numpadDialog(val) {
      if (!this.useMobileNumpad) {
        if (!val) this.destroyMobileOverlay();
        return;
      }
      if (val) this.mountMobileOverlay();
      else this.destroyMobileOverlay();
    },
    mobileNumpadDraft() {
      this.syncMobileOverlayDisplay();
    },
    numpadLable() {
      this.syncMobileOverlayDisplay();
    },
  },
  beforeDestroy() {
    this.destroyMobileOverlay();
  },
  methods: {
    setFocus() {
      this.$refs.refinput.focus();
    },
    formatDraft(raw) {
      const num = parseFloat(raw);
      if (!Number.isFinite(num)) return "0";
      if (typeof this.$numberFormat === "function") {
        return this.$numberFormat(num, "plain", 0);
      }
      return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    parseDraft(data) {
      if (data === "" || data == null || data === ".") return 0;
      const num = parseFloat(data);
      return Number.isFinite(num) ? num : 0;
    },
    openNumpad() {
      if (this.disabled || this.numpadDialog) return;
      const current =
        this.value === "0.00" || this.value == null || this.value === ""
          ? "0"
          : String(this.value);
      this.numpadValue = current;
      this.numpadLable = this.label ? this.$t(this.label) : this.$t("amount");
      if (this.useMobileNumpad) {
        this.mobileNumpadDraft = current;
        this.mobileNumpadPrevious = this.parseDraft(current);
        this.mobileNumpadReplaceNext = true;
      }
      this.numpadDialog = true;
      this.$nextTick(() => {
        const input = this.$refs.refinput;
        if (input && typeof input.blur === "function") {
          input.blur();
        }
      });
    },
    setNumpad(data) {
      this.cmpValue = data;
      this.numpadDialog = false;
    },
    onMobileFieldClick(e) {
      if (!this.useMobileNumpad || this.disabled) return;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.openNumpad();
    },
    mobileNumpadDigit(num) {
      this.mobileNumpadAppend(String(num));
    },
    mobileNumpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.mobileNumpadDraft).length + piece.length > 16) return;
      if (this.mobileNumpadReplaceNext) {
        this.mobileNumpadDraft = piece;
        this.mobileNumpadReplaceNext = false;
      } else if (this.mobileNumpadDraft === "0" && /^\d+$/.test(piece)) {
        this.mobileNumpadDraft = piece;
      } else {
        this.mobileNumpadDraft = `${this.mobileNumpadDraft}${piece}`;
      }
    },
    mobileNumpadDot() {
      if (this.mobileNumpadReplaceNext) {
        this.mobileNumpadDraft = "0.";
        this.mobileNumpadReplaceNext = false;
        return;
      }
      if (String(this.mobileNumpadDraft).includes(".")) return;
      this.mobileNumpadDraft = `${this.mobileNumpadDraft}.`;
    },
    mobileNumpadBack() {
      const current = String(this.mobileNumpadDraft || "0");
      if (current.length <= 1) {
        this.mobileNumpadDraft = "0";
        this.mobileNumpadReplaceNext = true;
      } else {
        this.mobileNumpadDraft = current.slice(0, -1);
        this.mobileNumpadReplaceNext = false;
      }
    },
    mobileNumpadClear() {
      this.mobileNumpadDraft = "0";
      this.mobileNumpadReplaceNext = true;
    },
    mobileNumpadStep(delta) {
      const next = this.parseDraft(this.mobileNumpadDraft) + delta;
      const safe = next < 0 ? 0 : next;
      this.mobileNumpadDraft = String(safe);
      this.mobileNumpadReplaceNext = true;
    },
    confirmMobileNumpad() {
      const value = this.parseDraft(this.mobileNumpadDraft);
      this.cmpValue = String(value);
      this.numpadDialog = false;
      this.$emit("blur");
    },
    cancelMobileNumpad() {
      this.numpadDialog = false;
    },
    syncMobileOverlayDisplay() {
      const root = this._mobileOverlayEl;
      if (!root) return;
      const title = root.querySelector("[data-money-title]");
      const label = root.querySelector("[data-money-label]");
      const value = root.querySelector("[data-money-value]");
      const text = this.numpadLable || "";
      if (title) title.textContent = text;
      if (label) label.textContent = text;
      if (value) value.textContent = this.mobileNumpadDisplay;
    },
    mountMobileOverlay() {
      this.destroyMobileOverlay();
      const el = document.createElement("div");
      el.className = "money-mobile-numpad-overlay";
      el.setAttribute("dir", "rtl");
      el.innerHTML = `
        <div class="money-mobile-numpad-overlay__backdrop" data-money-cancel="1"></div>
        <div class="money-mobile-numpad-panel" role="dialog" aria-modal="true">
          <div class="money-mobile-numpad-panel__handle"></div>
          <div class="money-mobile-numpad-panel__header">
            <span class="money-mobile-numpad-panel__spacer"></span>
            <div class="money-mobile-numpad-panel__title" data-money-title></div>
            <button type="button" class="money-mobile-numpad-panel__back" data-money-cancel="1" aria-label="back">
              <span class="money-mobile-numpad-panel__back-icon">←</span>
            </button>
          </div>
          <div class="money-mobile-numpad-panel__display">
            <div class="money-mobile-numpad-panel__display-label" data-money-label></div>
            <div class="money-mobile-numpad-panel__display-value" data-money-value></div>
          </div>
          <div class="money-mobile-numpad-panel__pad" dir="ltr">
            <div class="money-mobile-numpad-panel__digits">
              ${[7, 8, 9, 4, 5, 6, 1, 2, 3]
                .map(
                  (n) =>
                    `<button type="button" class="money-mobile-numpad-panel__key" data-money-digit="${n}">${n}</button>`
                )
                .join("")}
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--multi" data-money-append="00">00</button>
              <button type="button" class="money-mobile-numpad-panel__key" data-money-digit="0">0</button>
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--multi" data-money-append="000">000</button>
            </div>
            <div class="money-mobile-numpad-panel__actions">
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--action" data-money-step="1">+</button>
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--action" data-money-step="-1">−</button>
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--action" data-money-clear="1">C</button>
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--action" data-money-dot="1">.</button>
              <button type="button" class="money-mobile-numpad-panel__key money-mobile-numpad-panel__key--action" data-money-backspace="1">⌫</button>
            </div>
          </div>
          <div class="money-mobile-numpad-panel__footer">
            <button type="button" class="money-mobile-numpad-panel__confirm" data-money-confirm="1">
              ${this.$t("confirm")}
            </button>
          </div>
        </div>
      `;

      let lastTouchAt = 0;
      const onTap = (e) => {
        const target =
          e.target && e.target.closest
            ? e.target.closest(
                "[data-money-cancel],[data-money-digit],[data-money-append],[data-money-step],[data-money-clear],[data-money-dot],[data-money-backspace],[data-money-confirm]"
              )
            : null;
        if (!target) return;
        e.preventDefault();
        e.stopPropagation();
        if (target.hasAttribute("data-money-cancel")) {
          this.cancelMobileNumpad();
          return;
        }
        if (target.hasAttribute("data-money-confirm")) {
          this.confirmMobileNumpad();
          return;
        }
        if (target.hasAttribute("data-money-digit")) {
          this.mobileNumpadDigit(target.getAttribute("data-money-digit"));
          return;
        }
        if (target.hasAttribute("data-money-append")) {
          this.mobileNumpadAppend(target.getAttribute("data-money-append"));
          return;
        }
        if (target.hasAttribute("data-money-step")) {
          this.mobileNumpadStep(Number(target.getAttribute("data-money-step")));
          return;
        }
        if (target.hasAttribute("data-money-clear")) {
          this.mobileNumpadClear();
          return;
        }
        if (target.hasAttribute("data-money-dot")) {
          this.mobileNumpadDot();
          return;
        }
        if (target.hasAttribute("data-money-backspace")) {
          this.mobileNumpadBack();
        }
      };

      const onClick = (e) => {
        // Ignore ghost click that follows touchend.
        if (Date.now() - lastTouchAt < 450) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onTap(e);
      };
      const onTouchEnd = (e) => {
        if (!e || !e.changedTouches || !e.changedTouches.length) return;
        lastTouchAt = Date.now();
        onTap(e);
      };

      el.addEventListener("click", onClick, true);
      el.addEventListener("touchend", onTouchEnd, true);

      document.body.appendChild(el);
      this._mobileOverlayEl = el;
      this._mobileOverlayOnClick = onClick;
      this._mobileOverlayOnTouchEnd = onTouchEnd;
      this.syncMobileOverlayDisplay();
      document.body.style.overflow = "hidden";
    },
    destroyMobileOverlay() {
      const el = this._mobileOverlayEl;
      if (el) {
        if (this._mobileOverlayOnClick) {
          el.removeEventListener("click", this._mobileOverlayOnClick, true);
        }
        if (this._mobileOverlayOnTouchEnd) {
          el.removeEventListener("touchend", this._mobileOverlayOnTouchEnd, true);
        }
        if (el.parentNode) el.parentNode.removeChild(el);
      }
      this._mobileOverlayEl = null;
      this._mobileOverlayOnClick = null;
      this._mobileOverlayOnTouchEnd = null;
      document.body.style.overflow = "";
    },
    precision(a) {
      if (!isFinite(a)) return 0;
      var e = 1;
      var p = 0;
      while (Math.round(a * e) / e !== a) {
        e *= 10;
        p++;
      }
      return p;
    },
    ParseFloat(str, val) {
      str = str.toString();
      str = str.slice(0, str.indexOf(".") + val + 1);
      return Number(str);
    },
    humanFormat: function (number) {
      if (isNaN(number)) {
        number = "";
      } else {
        number = Number(number).toLocaleString(this.options.locale, {
          maximumFractionDigits: this.options.precision,
          minimumFractionDigits: this.options.precision,
        });
      }
      return number;
    },
    machineFormat(number) {
      if (number) {
        if (this.options.precision === undefined) {
          this.options.precision = 0;
        }
        number = this.cleanNumber(number);
        number = number.padStart(parseInt(this.options.precision) + 1, "0");
        number =
          number.substring(0, number.length - parseInt(this.options.precision)) +
          "." +
          number.substring(
            number.length - parseInt(this.options.precision),
            number.length
          );
        if (isNaN(number)) {
          number = this.valueWhenIsEmpty;
        }
      } else {
        number = this.valueWhenIsEmpty;
      }
      if (this.options.precision === 0) {
        number = this.cleanNumber(number);
      }
      return number;
    },
    isFloat(n) {
      return Number(n) === n && n % 1 !== 0;
    },
    keyPress($event) {
      if (this.useMobileNumpad) {
        $event.preventDefault();
        return;
      }
      const keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if (keyCode === 46 && this.options.precision === 0) {
        $event.preventDefault();
      }
      if (
        keyCode === 46 &&
        this.options.precision > 0 &&
        this.isFloat(parseFloat(this.value))
      ) {
        $event.preventDefault();
      }
      if (keyCode < 1000 && (keyCode < 48 || keyCode > 57) && keyCode !== 46) {
        $event.preventDefault();
      }
      if (keyCode > 1000 && (keyCode < 1776 || keyCode > 1785) && keyCode !== 46) {
        $event.preventDefault();
      }
    },
    cleanNumber: function (value) {
      let result = "";
      if (value) {
        let flag = false;
        const arrayValue = value.toString().split("");
        for (var i = 0; i < arrayValue.length; i++) {
          if (this.isInteger(arrayValue[i])) {
            if (!flag) {
              if (arrayValue[i] !== "0") {
                result = result + arrayValue[i];
                flag = true;
              }
            } else {
              result = result + arrayValue[i];
            }
          }
        }
      }
      return result;
    },
    isInteger(value) {
      let result = false;
      if (Number.isInteger(parseInt(value))) {
        result = true;
      }
      return result;
    },
    targetLength() {
      if (Number(this.cleanNumber(this.value).length) >= Number(this.options.length)) {
        return true;
      } else {
        return false;
      }
    },
    onBlur(e) {
      if (this.value) {
        var str = this.value.toString().split(".");
        if (str[1] > 0) {
          var app = this;
          setTimeout(function () {
            app.cmpValue =
              str[0].toString() +
              "." +
              str[1].substr(0, app.options.precision).toString();
          }, 2);
        }
        if (this.value.length === 0 || parseFloat(this.value) <= this.valueOptions.min) {
          this.$emit(this.valueOptions.minEvent || "SetValueMin", this.valueOptions.min);
        }
        if (
          typeof this.valueOptions.max !== "undefined" &&
          parseFloat(this.value) > parseFloat(this.valueOptions.max)
        ) {
          this.$emit(this.valueOptions.maxEvent || "SetValueMax", this.valueOptions.max);
        }
      }
      this.$emit("blur", e);
    },
    onFocus(e) {
      if (this.useMobileNumpad) {
        this.openNumpad();
        return;
      }
      if (!this.autofocus && this.focusEvent) {
        this.focusEvent(e);
      }
      this.$emit("focus", e);
    },
  },
};
</script>
<style scoped>
.money-root--mobile-numpad {
  cursor: pointer;
}

.money-root--mobile-numpad ::v-deep input {
  pointer-events: none;
  caret-color: transparent;
}

.input-new-style ::v-deep .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
}
.input-new-style ::v-deep fieldset {
  border: none !important;
}
.input-new-style ::v-deep .v-input__slot::before,
.input-new-style ::v-deep .v-input__slot::after {
  display: none !important;
}
</style>

<style>
/* Body-mounted overlay — escapes nested dialog transforms */
.money-mobile-numpad-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: auto;
}

.money-mobile-numpad-overlay__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(38, 50, 56, 0.42);
}

.money-mobile-numpad-panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-height: min(92vh, 620px);
  display: flex;
  flex-direction: column;
  padding: 6px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-radius: 16px 16px 0 0;
  background: #f3f5f7;
  box-shadow: 0 -8px 24px rgba(38, 50, 56, 0.18);
  box-sizing: border-box;
}

.money-mobile-numpad-panel__handle {
  width: 36px;
  height: 4px;
  margin: 2px auto 8px;
  border-radius: 999px;
  background: #cfd8dc;
  flex: 0 0 auto;
}

.money-mobile-numpad-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  flex: 0 0 auto;
}

.money-mobile-numpad-panel__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}

.money-mobile-numpad-panel__spacer {
  flex: 0 0 40px;
  width: 40px;
}

.money-mobile-numpad-panel__back {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #37474f;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.money-mobile-numpad-panel__back-icon {
  display: inline-block;
  transform: scaleX(1.1);
}

.money-mobile-numpad-panel__display {
  margin-bottom: 10px;
  padding: 10px 14px 12px;
  border-radius: 12px;
  border: 1px solid #d5dde8;
  background: linear-gradient(180deg, #f8f9fc 0%, #eef1f6 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
  flex: 0 0 auto;
}

.money-mobile-numpad-panel__display-label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #8a96a8;
  text-align: right;
}

.money-mobile-numpad-panel__display-value {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 28px;
  font-weight: 800;
  color: #5a6b85;
  direction: ltr;
  unicode-bidi: plaintext;
  letter-spacing: 0.03em;
  line-height: 1.1;
}

.money-mobile-numpad-panel__pad {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 6px;
  margin-bottom: 8px;
  flex: 1 1 auto;
  min-height: 0;
}

.money-mobile-numpad-panel__digits {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.money-mobile-numpad-panel__actions {
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 6px;
}

.money-mobile-numpad-panel__key {
  min-height: 44px;
  height: 100%;
  border: 0;
  border-radius: 10px;
  background: #fff;
  color: #263238;
  font-size: 20px;
  font-weight: 800;
  box-shadow: 0 1px 0 rgba(55, 71, 79, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
}

.money-mobile-numpad-panel__key:active {
  background: #eceff1;
}

.money-mobile-numpad-panel__key--multi {
  font-size: 15px;
}

.money-mobile-numpad-panel__key--action {
  background: #e8edf5;
  color: #5a6b85;
  font-size: 17px;
  border: 1px solid #d0d8e4;
}

.money-mobile-numpad-panel__key--action:active {
  background: #d9e1ec;
}

.money-mobile-numpad-panel__footer {
  flex: 0 0 auto;
  padding-top: 2px;
}

.money-mobile-numpad-panel__confirm {
  width: 100%;
  min-height: 48px;
  border: 1px solid #c5d0e0;
  border-radius: 12px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
}

.money-mobile-numpad-panel__confirm:active {
  background: #d9e1ec;
}
</style>
